import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useLocale } from '@/contexts/LocaleContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { 
  Sparkles, Loader2, Upload, Download, Wand2, Palette, 
  Image as ImageIcon, Save, ChevronRight, ChevronLeft, Shirt, Scissors,
  RotateCcw, Plus, FolderPlus, Eye, X, ZoomIn, Check
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Safe fetch wrapper using XMLHttpRequest to avoid fetch interceptors
const safeFetch = (url, options = {}) => {
  return new Promise((resolve, reject) => {
    const token = localStorage.getItem('texhub_token') || sessionStorage.getItem('texhub_token');
    const xhr = new XMLHttpRequest();
    
    xhr.open(options.method || 'GET', url, true);
    
    // Set headers
    xhr.setRequestHeader('Content-Type', 'application/json');
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }
    if (options.headers) {
      Object.entries(options.headers).forEach(([key, value]) => {
        if (key.toLowerCase() !== 'content-type') {
          xhr.setRequestHeader(key, value);
        }
      });
    }
    
    xhr.onload = () => {
      let data;
      try {
        data = xhr.responseText ? JSON.parse(xhr.responseText) : {};
      } catch (e) {
        console.error('Failed to parse response:', xhr.responseText?.substring(0, 100));
        data = { detail: 'Error procesando respuesta del servidor' };
      }
      resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
    };
    
    xhr.onerror = () => {
      reject(new Error('Error de red'));
    };
    
    xhr.ontimeout = () => {
      reject(new Error('Timeout'));
    };
    
    // Set timeout (60 seconds for AI operations)
    xhr.timeout = 60000;
    
    // Send request
    if (options.body) {
      xhr.send(options.body);
    } else {
      xhr.send();
    }
  });
};

const GARMENT_TYPES = [
  { id: 'camisa', name: 'Camisa', nameEn: 'Shirt', icon: '👔' },
  { id: 'playera', name: 'Playera', nameEn: 'T-Shirt', icon: '👕' },
  { id: 'blusa', name: 'Blusa', nameEn: 'Blouse', icon: '👚' },
  { id: 'vestido', name: 'Vestido', nameEn: 'Dress', icon: '👗' },
  { id: 'pantalon', name: 'Pantalón', nameEn: 'Pants', icon: '👖' },
  { id: 'falda', name: 'Falda', nameEn: 'Skirt', icon: '🩱' },
  { id: 'blazer', name: 'Blazer/Saco', nameEn: 'Blazer', icon: '🧥' },
  { id: 'chamarra', name: 'Chamarra', nameEn: 'Jacket', icon: '🧥' },
  { id: 'sudadera', name: 'Sudadera', nameEn: 'Hoodie', icon: '👕' },
  { id: 'abrigo', name: 'Abrigo', nameEn: 'Coat', icon: '🧥' },
];

const STYLE_OPTIONS = [
  { id: 'realistic', name: 'Fotorrealista', nameEn: 'Photorealistic', desc: 'Fotografía profesional de moda' },
  { id: 'sketch', name: 'Boceto', nameEn: 'Sketch', desc: 'Ilustración de diseño de moda' },
  { id: 'technical', name: 'Técnico', nameEn: 'Technical', desc: 'Dibujo técnico/plano' },
];

const FashionDesigner = ({ onSendToOptimizer, initialImage, onImageProcessed, onCreditError, onSendToTexChat }) => {
  const { isUSA } = useLocale();
  
  // State - Simplified flow: 1: Create/Edit, 2: Results
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [designs, setDesigns] = useState([]);
  const [currentDesign, setCurrentDesign] = useState(null);
  
  // Step 1: Form fields
  const [patternImage, setPatternImage] = useState(null);
  const [patternPreview, setPatternPreview] = useState(null);
  const [prompt, setPrompt] = useState('');
  const [garmentType, setGarmentType] = useState('');
  const [style, setStyle] = useState('realistic');
  
  // Fabric (now in Step 1)
  const [fabricImage, setFabricImage] = useState(null);
  const [fabricPreview, setFabricPreview] = useState(null);
  
  // Edit
  const [editPrompt, setEditPrompt] = useState('');
  
  // Fabric variation result
  const [fabricVariation, setFabricVariation] = useState(null);
  
  // Save dialog
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [productName, setProductName] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Image modal for click-to-enlarge
  const [imageModal, setImageModal] = useState(null);
  
  // Color change feature (after applying fabric)
  const [showColorChange, setShowColorChange] = useState(false);
  const [colorInput, setColorInput] = useState(''); // Text input for color
  const [colorInputType, setColorInputType] = useState('text'); // 'text', 'pantone', 'photo'
  const [colorReferenceImage, setColorReferenceImage] = useState(null);
  const [colorReferencePreview, setColorReferencePreview] = useState(null);
  const [generatingColor, setGeneratingColor] = useState(false);
  const [colorResult, setColorResult] = useState(null);
  
  const patternInputRef = useRef(null);
  const fabricInputRef = useRef(null);
  const colorReferenceInputRef = useRef(null);

  // Close image modal with Escape key
  useEffect(() => {
    if (!imageModal) return;
    
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setImageModal(null);
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [imageModal]);

  // Load user's designs on mount and check for TEXTREND pattern
  useEffect(() => {
    fetchDesigns();
    
    // Check if there's a pattern from TEXTREND
    const textrendPattern = sessionStorage.getItem('textrend_pattern');
    if (textrendPattern) {
      try {
        const patternData = JSON.parse(textrendPattern);
        if (patternData.from_textrend) {
          setPatternImage(patternData.image_base64);
          setPatternPreview(`data:image/jpeg;base64,${patternData.image_base64}`);
          setPrompt(patternData.description || patternData.pattern_name || '');
          
          toast.success(
            isUSA 
              ? `Pattern "${patternData.pattern_name}" loaded from TEXTREND!` 
              : `¡Estampado "${patternData.pattern_name}" cargado desde TEXTREND!`
          );
          
          sessionStorage.removeItem('textrend_pattern');
        }
      } catch (e) {
        console.error('Error loading TEXTREND pattern:', e);
      }
    }
    
    // Listen for switch-to-designer event from TEXTREND
    const handleSwitchToDesigner = () => {
      const newPattern = sessionStorage.getItem('textrend_pattern');
      if (newPattern) {
        try {
          const patternData = JSON.parse(newPattern);
          setPatternImage(patternData.image_base64);
          setPatternPreview(`data:image/jpeg;base64,${patternData.image_base64}`);
          setPrompt(patternData.description || patternData.pattern_name || '');
          setStep(1);
          sessionStorage.removeItem('textrend_pattern');
        } catch (e) {
          console.error('Error loading TEXTREND pattern:', e);
        }
      }
    };
    
    window.addEventListener('switch-to-designer', handleSwitchToDesigner);
    return () => window.removeEventListener('switch-to-designer', handleSwitchToDesigner);
  }, [isUSA]);

  // Load initial image from Photo Optimizer via sessionStorage (runs only on mount)
  useEffect(() => {
    const optimizerData = sessionStorage.getItem('optimizer_to_designer');
    if (optimizerData) {
      try {
        const data = JSON.parse(optimizerData);
        if (data.image_base64) {
          if (data.type === 'fabric') {
            // Load as fabric texture for "Apply Fabric" section
            setFabricImage(data.image_base64);
            setFabricPreview(`data:image/png;base64,${data.image_base64}`);
            setStep(1);
            toast.success('¡Imagen cargada como textura de tela! Úsala para aplicar a tus diseños.');
          } else {
            // Default: Load as pattern reference
            setPatternImage(data.image_base64);
            setPatternPreview(`data:image/png;base64,${data.image_base64}`);
            setStep(1);
            toast.success('¡Imagen cargada como referencia! Describe tu diseño.');
          }
          sessionStorage.removeItem('optimizer_to_designer');
        }
      } catch (e) {
        console.error('Error loading image from optimizer:', e);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only on mount
  
  // Load initial image from props (for backwards compatibility)
  useEffect(() => {
    if (initialImage) {
      setPatternImage(initialImage);
      setPatternPreview(`data:image/png;base64,${initialImage}`);
      setStep(1);
      toast.success(isUSA ? 'Image loaded as pattern reference! Describe your design.' : '¡Imagen cargada como referencia! Describe tu diseño.');
      setTimeout(() => {
        if (onImageProcessed) {
          onImageProcessed();
        }
      }, 100);
    }
  }, [initialImage, onImageProcessed, isUSA]);

  const fetchDesigns = async () => {
    try {
      const { ok, data } = await safeFetch(`${API_URL}/api/texvision/fashion/designs`);
      if (ok) {
        setDesigns(data);
      }
    } catch (e) {
      console.error('Error fetching designs:', e);
    }
  };

  const handlePatternUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target.result.split(',')[1];
        setPatternImage(base64);
        setPatternPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Compress image to reduce size for mobile
  const compressImage = (base64, maxWidth = 800, quality = 0.7) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        const compressed = canvas.toDataURL('image/jpeg', quality).split(',')[1];
        resolve(compressed);
      };
      img.src = `data:image/jpeg;base64,${base64}`;
    });
  };

  const handleFabricUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target.result.split(',')[1];
        // Compress fabric image for faster upload
        const compressed = await compressImage(base64, 800, 0.8);
        setFabricImage(compressed);
        setFabricPreview(`data:image/jpeg;base64,${compressed}`);
        toast.success(isUSA ? 'Fabric image loaded!' : '¡Imagen de tela cargada!');
      };
      reader.readAsDataURL(file);
    }
  };

  // Generate design from prompt or pattern (with optional fabric)
  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error(isUSA ? 'Please describe your design' : 'Por favor describe tu diseño');
      return;
    }

    setLoading(true);
    setFabricVariation(null);
    
    try {
      // First generate the base design
      const { ok, data } = await safeFetch(`${API_URL}/api/texvision/fashion/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: prompt,
          pattern_image_base64: patternImage || null,
          style: style,
          garment_type: garmentType || null
        })
      });
      
      if (!ok) {
        throw new Error(data.detail || 'Error al generar');
      }

      setCurrentDesign(data);
      
      // If fabric was uploaded, apply it immediately (generate 1 variation)
      if (fabricImage) {
        toast.info(isUSA ? 'Applying fabric...' : 'Aplicando tela...');
        try {
          const fabricResult = await safeFetch(`${API_URL}/api/texvision/fashion/apply-fabric`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              design_id: data.design_id,
              fabric_image_base64: fabricImage,
              num_variations: 1
            })
          });
          
          if (fabricResult.ok && fabricResult.data.variations?.length > 0) {
            setFabricVariation(fabricResult.data.variations[0]);
          } else if (!fabricResult.ok) {
            toast.error(fabricResult.data.detail || (isUSA ? 'Failed to apply fabric' : 'Error al aplicar tela'));
          }
        } catch (fabricError) {
          console.error('Fabric error:', fabricError);
          toast.error(isUSA ? 'Error applying fabric' : 'Error al aplicar tela');
        }
      }
      
      setStep(2);
      toast.success(isUSA ? 'Design generated!' : '¡Diseño generado!');
      fetchDesigns();
    } catch (e) {
      if (onCreditError && onCreditError(e.message)) {
        // Error was handled by parent
      } else {
        toast.error(e.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Edit design
  const handleEdit = async () => {
    if (!editPrompt.trim() || !currentDesign) {
      toast.error(isUSA ? 'Please describe the changes' : 'Por favor describe los cambios');
      return;
    }

    setLoading(true);
    try {
      const { ok, data } = await safeFetch(`${API_URL}/api/texvision/fashion/edit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          design_id: currentDesign.design_id,
          edit_prompt: editPrompt
        })
      });
      
      if (!ok) {
        throw new Error(data.detail || 'Error al editar');
      }

      setCurrentDesign({ ...currentDesign, image_base64: data.image_base64 });
      setEditPrompt('');
      setFabricVariation(null);
      toast.success(isUSA ? 'Design updated!' : '¡Diseño actualizado!');
    } catch (e) {
      if (onCreditError && onCreditError(e.message)) {
        // Error was handled by parent
      } else {
        toast.error(e.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Apply fabric (generates 1 variation)
  const handleApplyFabric = async () => {
    if (!fabricImage || !currentDesign) {
      toast.error(isUSA ? 'Please upload a fabric image' : 'Por favor sube una imagen de tela');
      return;
    }

    setLoading(true);
    try {
      const { ok, status, data } = await safeFetch(`${API_URL}/api/texvision/fashion/apply-fabric`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          design_id: currentDesign.design_id,
          fabric_image_base64: fabricImage,
          num_variations: 1
        })
      });
      
      if (!ok) {
        if (status === 402) {
          throw new Error(isUSA ? 'AI budget exceeded. Please contact support.' : 'Presupuesto de IA agotado. Contacta al soporte.');
        }
        throw new Error(data.detail || 'Error al aplicar tela');
      }

      if (data.variations && data.variations.length > 0) {
        setFabricVariation(data.variations[0]);
      }
      toast.success(isUSA ? 'Fabric applied!' : '¡Tela aplicada!');
    } catch (e) {
      if (onCreditError && onCreditError(e.message)) {
        // Error was handled by parent
      } else {
        toast.error(e.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Save to catalog
  const handleSaveToCatalog = async () => {
    if (!productName.trim() || !currentDesign) return;

    setSaving(true);
    try {
      const { ok, data } = await safeFetch(`${API_URL}/api/texvision/fashion/save-to-catalog?design_id=${currentDesign.design_id}&product_name=${encodeURIComponent(productName)}`, {
        method: 'POST'
      });
      
      if (!ok) {
        throw new Error(data.detail || 'Error al guardar');
      }

      toast.success(isUSA ? 'Saved to catalog!' : '¡Guardado en tu catálogo!');
      setShowSaveDialog(false);
      setProductName('');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const downloadImage = (base64, filename = 'design.png') => {
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${base64}`;
    link.download = filename;
    link.click();
  };

  // Handle color reference image upload
  const handleColorReferenceUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error(isUSA ? 'Image must be less than 5MB' : 'La imagen debe ser menor a 5MB');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target.result.split(',')[1];
      setColorReferenceImage(base64);
      setColorReferencePreview(event.target.result);
    };
    reader.readAsDataURL(file);
  };

  // Generate color change - uses the current design/fabric variation
  const handleColorChange = async () => {
    if (!colorInput && !colorReferenceImage) {
      toast.error(isUSA ? 'Please specify a color' : 'Por favor especifica un color');
      return;
    }
    
    const imageToRecolor = fabricVariation?.image_base64 || currentDesign?.image_base64;
    if (!imageToRecolor) {
      toast.error(isUSA ? 'No design to recolor' : 'No hay diseño para cambiar de color');
      return;
    }
    
    setGeneratingColor(true);
    setColorResult(null);
    
    try {
      const { ok, data } = await safeFetch(`${API_URL}/api/texvision/fashion/change-color`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          design_image_base64: imageToRecolor,
          color_description: colorInput,
          color_type: colorInputType,
          color_reference_base64: colorReferenceImage || null
        })
      });
      
      if (!ok) {
        throw new Error(data.detail || 'Error al cambiar color');
      }
      
      setColorResult(data.image_base64);
      toast.success(isUSA ? 'Color changed!' : '¡Color cambiado!');
    } catch (e) {
      if (onCreditError && onCreditError(e.message)) {
        // Error handled by parent
      } else {
        toast.error(e.message);
      }
    } finally {
      setGeneratingColor(false);
    }
  };

  // Apply the color result as the new fabric variation
  const applyColorResult = async () => {
    if (colorResult && currentDesign) {
      try {
        // Save the color variation as a new design in history
        const { ok, data } = await safeFetch(`${API_URL}/api/texvision/fashion/save-color-variation`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            original_design_id: currentDesign.design_id,
            color_image_base64: colorResult,
            color_description: colorInput || 'Color variation'
          })
        });
        
        if (ok && data.design_id) {
          // Update current design with new color
          setCurrentDesign({
            ...currentDesign,
            design_id: data.design_id,
            image_base64: colorResult
          });
          setFabricVariation(null); // Clear fabric variation since we now have a new base
          fetchDesigns(); // Refresh the designs list
        } else {
          // Fallback: just update locally
          setFabricVariation({ image_base64: colorResult });
        }
        
        setShowColorChange(false);
        setColorResult(null);
        setColorInput('');
        setColorReferenceImage(null);
        setColorReferencePreview(null);
        toast.success(isUSA ? 'Color applied and saved!' : '¡Color aplicado y guardado!');
      } catch (e) {
        console.error('Error saving color variation:', e);
        // Fallback: just update locally
        setFabricVariation({ image_base64: colorResult });
        setShowColorChange(false);
        setColorResult(null);
        setColorInput('');
        toast.success(isUSA ? 'Color applied!' : '¡Color aplicado!');
      }
    }
  };

  const resetDesigner = () => {
    setStep(1);
    setCurrentDesign(null);
    setPatternImage(null);
    setPatternPreview(null);
    setPrompt('');
    setGarmentType('');
    setStyle('realistic');
    setEditPrompt('');
    setFabricImage(null);
    setFabricPreview(null);
    setFabricVariation(null);
  };

  // Get the current display image (fabric variation if available, otherwise base design)
  const getCurrentDisplayImage = () => {
    if (fabricVariation) return fabricVariation.image_base64;
    if (currentDesign) return currentDesign.image_base64;
    return null;
  };

  return (
    <div className="space-y-6" data-testid="fashion-designer">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Shirt className="w-6 h-6 text-purple-600" />
            {isUSA ? 'Fashion Designer AI' : 'Diseñador de Modas AI'}
          </h2>
          <p className="text-slate-500 text-sm mt-1">
            {isUSA 
              ? 'Create, edit and visualize your clothing designs with AI' 
              : 'Crea, edita y visualiza tus diseños de ropa con IA'}
          </p>
        </div>
        {currentDesign && (
          <Button variant="outline" onClick={resetDesigner}>
            <RotateCcw className="w-4 h-4 mr-2" />
            {isUSA ? 'New Design' : 'Nuevo Diseño'}
          </Button>
        )}
      </div>

      {/* Progress Steps - Simplified */}
      <div className="flex items-center justify-center gap-2 py-4">
        {[
          { num: 1, label: isUSA ? 'Create' : 'Crear' },
          { num: 2, label: isUSA ? 'Result & Edit' : 'Resultado y Editar' },
        ].map((s, i) => (
          <React.Fragment key={s.num}>
            <div 
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                step >= s.num 
                  ? 'bg-purple-100 text-purple-700' 
                  : 'bg-slate-100 text-slate-400'
              }`}
            >
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${
                step >= s.num ? 'bg-purple-600 text-white' : 'bg-slate-300 text-slate-500'
              }`}>
                {s.num}
              </div>
              <span className="text-sm font-medium">{s.label}</span>
            </div>
            {i < 1 && <ChevronRight className="w-5 h-5 text-slate-300" />}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1: Create Design Form */}
      {step === 1 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Scissors className="w-5 h-5" />
                {isUSA ? 'Design Your Garment' : 'Diseña tu Prenda'}
              </CardTitle>
              <CardDescription>
                {isUSA 
                  ? 'Describe your design and optionally upload a pattern or fabric' 
                  : 'Describe tu diseño y opcionalmente sube un patrón o tela'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Description Prompt */}
              <div>
                <Label className="text-base font-semibold">{isUSA ? 'Describe Your Design' : 'Describe tu Diseño'} *</Label>
                <Textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder={isUSA 
                    ? 'E.g.: Elegant long dress with V-neck, flowing silhouette, sleeveless, floor length...'
                    : 'Ej: Vestido largo elegante con escote en V, silueta fluida, sin mangas, hasta el piso...'}
                  rows={3}
                  className="mt-2"
                />
              </div>

              {/* Garment Type */}
              <div>
                <Label>{isUSA ? 'Garment Type' : 'Tipo de Prenda'}</Label>
                <Select value={garmentType} onValueChange={setGarmentType}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder={isUSA ? 'Select type (optional)...' : 'Seleccionar tipo (opcional)...'} />
                  </SelectTrigger>
                  <SelectContent>
                    {GARMENT_TYPES.map(g => (
                      <SelectItem key={g.id} value={g.id}>
                        {g.icon} {isUSA ? g.nameEn : g.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Style */}
              <div>
                <Label>{isUSA ? 'Visualization Style' : 'Estilo de Visualización'}</Label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {STYLE_OPTIONS.map(s => (
                    <button
                      key={s.id}
                      onClick={() => setStyle(s.id)}
                      className={`p-3 rounded-lg border text-center transition-all ${
                        style === s.id 
                          ? 'border-purple-500 bg-purple-50 text-purple-700' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <span className="text-sm font-medium block">{isUSA ? s.nameEn : s.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Divider */}
              <div className="relative flex items-center py-2">
                <div className="flex-1 border-t border-slate-200"></div>
                <span className="px-3 text-sm text-slate-400 font-medium">{isUSA ? 'Optional uploads' : 'Archivos opcionales'}</span>
                <div className="flex-1 border-t border-slate-200"></div>
              </div>

              {/* Pattern Upload */}
              <div>
                <Label className="flex items-center gap-2">
                  <Scissors className="w-4 h-4" />
                  {isUSA ? 'Pattern Image (Optional)' : 'Imagen de Patrón (Opcional)'}
                </Label>
                <p className="text-xs text-slate-500 mb-2">
                  {isUSA 
                    ? 'Upload a photo of your physical pattern' 
                    : 'Sube una foto de tu patrón físico'}
                </p>
                <input
                  type="file"
                  accept="image/*"
                  ref={patternInputRef}
                  onChange={handlePatternUpload}
                  className="hidden"
                />
                {patternPreview ? (
                  <div className="relative">
                    <img 
                      src={patternPreview} 
                      alt="Pattern" 
                      className="w-full h-32 object-contain rounded-lg border cursor-pointer hover:opacity-90"
                      onClick={() => setImageModal({ src: patternPreview, title: isUSA ? 'Pattern' : 'Patrón' })}
                    />
                    <Button 
                      size="sm" 
                      variant="destructive" 
                      className="absolute top-2 right-2 w-7 h-7 p-0"
                      onClick={() => { setPatternImage(null); setPatternPreview(null); }}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <Button 
                    variant="outline" 
                    className="w-full h-20 border-dashed"
                    onClick={() => patternInputRef.current?.click()}
                  >
                    <Upload className="w-5 h-5 mr-2" />
                    {isUSA ? 'Upload Pattern' : 'Subir Patrón'}
                  </Button>
                )}
              </div>

              {/* Fabric Upload - NOW IN STEP 1 */}
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <Label className="flex items-center gap-2 text-purple-700">
                  <Palette className="w-4 h-4" />
                  {isUSA ? 'Apply Fabric (Optional)' : 'Aplicar Tela (Opcional)'}
                </Label>
                <p className="text-xs text-purple-600 mb-2">
                  {isUSA 
                    ? 'Upload a fabric photo to see your design with that material' 
                    : 'Sube una foto de tela para ver tu diseño con ese material'}
                </p>
                <input
                  type="file"
                  accept="image/*"
                  ref={fabricInputRef}
                  onChange={handleFabricUpload}
                  className="hidden"
                />
                {fabricPreview ? (
                  <div className="relative">
                    <img 
                      src={fabricPreview} 
                      alt="Fabric" 
                      className="w-full h-24 object-cover rounded-lg border cursor-pointer hover:opacity-90"
                      onClick={() => setImageModal({ src: fabricPreview, title: isUSA ? 'Fabric' : 'Tela' })}
                    />
                    <Button 
                      size="sm" 
                      variant="destructive" 
                      className="absolute top-2 right-2 w-7 h-7 p-0"
                      onClick={() => { setFabricImage(null); setFabricPreview(null); }}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <Button 
                    variant="outline" 
                    className="w-full h-16 border-dashed border-purple-300 hover:border-purple-400 hover:bg-purple-100"
                    onClick={() => fabricInputRef.current?.click()}
                  >
                    <Upload className="w-5 h-5 mr-2" />
                    {isUSA ? 'Upload Fabric Photo' : 'Subir Foto de Tela'}
                  </Button>
                )}
              </div>

              {/* Generate Button */}
              <Button 
                className="w-full bg-purple-600 hover:bg-purple-700 py-6 text-lg"
                onClick={handleGenerate}
                disabled={loading || !prompt.trim()}
              >
                {loading ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> {isUSA ? 'Generating...' : 'Generando...'}</>
                ) : (
                  <><Sparkles className="w-5 h-5 mr-2" /> {isUSA ? 'Generate Design' : 'Generar Diseño'}</>
                )}
              </Button>

            </CardContent>
          </Card>

          {/* Recent Designs Preview */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ImageIcon className="w-5 h-5" />
                {isUSA ? 'Recent Designs' : 'Diseños Recientes'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {designs.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {designs.slice(0, 9).map(d => (
                    <button
                      key={d.design_id}
                      onClick={() => { setCurrentDesign(d); setStep(2); setFabricVariation(null); }}
                      className="relative group aspect-square rounded-lg overflow-hidden border hover:border-purple-500 transition-colors"
                    >
                      <img 
                        src={`data:image/png;base64,${d.image_base64}`}
                        alt="Design"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <Eye className="w-6 h-6 text-white" />
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-60 text-slate-400">
                  <Shirt className="w-16 h-16 mb-4 opacity-30" />
                  <p className="text-center">
                    {isUSA 
                      ? 'Your designs will appear here' 
                      : 'Tus diseños aparecerán aquí'}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Step 2: Results & Edit */}
      {step === 2 && currentDesign && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Preview */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ImageIcon className="w-5 h-5" />
                {isUSA ? 'Your Design' : 'Tu Diseño'}
                {fabricVariation && (
                  <Badge className="ml-2 bg-purple-100 text-purple-700">
                    {isUSA ? 'With Fabric' : 'Con Tela'}
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Main Preview - Click to enlarge */}
                <div 
                  className="relative bg-slate-100 rounded-xl overflow-hidden cursor-pointer group"
                  onClick={() => setImageModal({
                    src: `data:image/png;base64,${getCurrentDisplayImage()}`,
                    title: isUSA ? 'Design Preview' : 'Vista Previa del Diseño'
                  })}
                >
                  <img 
                    src={`data:image/png;base64,${getCurrentDisplayImage()}`}
                    alt="Design"
                    className="w-full aspect-square object-contain"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center pointer-events-none">
                    <div className="bg-white/90 rounded-full p-3 opacity-0 group-hover:opacity-100 transition-opacity">
                      <ZoomIn className="w-6 h-6 text-slate-700" />
                    </div>
                  </div>
                </div>
                
                {/* Show both images if fabric was applied */}
                {fabricVariation && currentDesign && (
                  <div className="flex gap-2">
                    <div 
                      className="flex-1 aspect-square rounded-lg overflow-hidden border-2 border-slate-200 cursor-pointer hover:border-purple-400"
                      onClick={() => setImageModal({
                        src: `data:image/png;base64,${currentDesign.image_base64}`,
                        title: isUSA ? 'Original Design' : 'Diseño Original'
                      })}
                    >
                      <img 
                        src={`data:image/png;base64,${currentDesign.image_base64}`}
                        alt="Original"
                        className="w-full h-full object-contain"
                      />
                      <p className="text-xs text-center text-slate-500 mt-1">{isUSA ? 'Original' : 'Original'}</p>
                    </div>
                    <div 
                      className="flex-1 aspect-square rounded-lg overflow-hidden border-2 border-purple-400 cursor-pointer hover:border-purple-500"
                      onClick={() => setImageModal({
                        src: `data:image/png;base64,${fabricVariation.image_base64}`,
                        title: isUSA ? 'With Fabric' : 'Con Tela'
                      })}
                    >
                      <img 
                        src={`data:image/png;base64,${fabricVariation.image_base64}`}
                        alt="With Fabric"
                        className="w-full h-full object-contain"
                      />
                      <p className="text-xs text-center text-purple-600 mt-1">{isUSA ? 'With Fabric' : 'Con Tela'}</p>
                    </div>
                  </div>
                )}
                
                {/* Action Buttons */}
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => downloadImage(getCurrentDisplayImage(), 'design.png')}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {isUSA ? 'Download' : 'Descargar'}
                  </Button>
                  <Button 
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    onClick={() => setShowSaveDialog(true)}
                  >
                    <FolderPlus className="w-4 h-4 mr-2" />
                    {isUSA ? 'Save to Catalog' : 'Guardar en Catálogo'}
                  </Button>
                </div>

                {/* Send to Optimizer */}
                {onSendToOptimizer && (
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => {
                      onSendToOptimizer(getCurrentDisplayImage());
                      toast.success(isUSA ? 'Image sent to Photo Optimizer' : 'Imagen enviada al Optimizador');
                    }}
                  >
                    <Wand2 className="w-4 h-4 mr-2" />
                    {isUSA ? 'Send to Photo Optimizer' : 'Enviar a Optimizador de Fotos'}
                  </Button>
                )}

                {/* Send to TexChat */}
                {onSendToTexChat && (
                  <Button 
                    variant="outline" 
                    className="w-full border-amber-300 text-amber-700 hover:bg-amber-50"
                    onClick={() => {
                      const contextData = {
                        garment_type: currentDesign.garmentType || garmentType,
                        description: currentDesign.prompt || prompt || ''
                      };
                      onSendToTexChat(contextData, getCurrentDisplayImage());
                      toast.success(isUSA ? 'Opening TexChat AI...' : 'Abriendo TexChat AI...');
                    }}
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    {isUSA ? 'Ask TexChat AI' : 'Preguntar a TexChat AI'}
                  </Button>
                )}

                {currentDesign.prompt && (
                  <div className="text-sm text-slate-500 bg-slate-50 p-3 rounded-lg">
                    <span className="font-medium">{isUSA ? 'Prompt:' : 'Descripción:'}</span> {currentDesign.prompt}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Right: Edit Controls */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wand2 className="w-5 h-5" />
                {isUSA ? 'Edit & Customize' : 'Editar y Personalizar'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Edit with Prompt */}
              <div>
                <Label className="text-base font-semibold">{isUSA ? 'Edit Design' : 'Editar Diseño'}</Label>
                <p className="text-xs text-slate-500 mb-2">
                  {isUSA 
                    ? 'Describe changes: color, size, style, details...' 
                    : 'Describe cambios: color, tamaño, estilo, detalles...'}
                </p>
                <Textarea
                  value={editPrompt}
                  onChange={(e) => setEditPrompt(e.target.value)}
                  placeholder={isUSA 
                    ? 'E.g.: Make sleeves shorter, add a belt, change to darker color...'
                    : 'Ej: Hacer mangas más cortas, agregar cinturón, cambiar a color más oscuro...'}
                  rows={3}
                />
                <Button 
                  className="w-full mt-3"
                  variant="outline"
                  onClick={handleEdit}
                  disabled={loading || !editPrompt.trim()}
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {isUSA ? 'Editing...' : 'Editando...'}</>
                  ) : (
                    <><Wand2 className="w-4 h-4 mr-2" /> {isUSA ? 'Apply Changes' : 'Aplicar Cambios'}</>
                  )}
                </Button>
              </div>

              {/* Apply Fabric Section */}
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <Label className="flex items-center gap-2 text-purple-700 text-base font-semibold">
                  <Palette className="w-4 h-4" />
                  {isUSA ? 'Apply Different Fabric' : 'Aplicar Tela Diferente'}
                </Label>
                <p className="text-xs text-purple-600 mb-3">
                  {isUSA 
                    ? 'Upload a new fabric to see the design with that material' 
                    : 'Sube una nueva tela para ver el diseño con ese material'}
                </p>
                <input
                  type="file"
                  accept="image/*"
                  ref={fabricInputRef}
                  onChange={handleFabricUpload}
                  className="hidden"
                />
                {fabricPreview ? (
                  <div className="space-y-3">
                    <div className="relative">
                      <img 
                        src={fabricPreview} 
                        alt="Fabric" 
                        className="w-full h-24 object-cover rounded-lg border cursor-pointer"
                        onClick={() => setImageModal({ src: fabricPreview, title: isUSA ? 'Fabric' : 'Tela' })}
                      />
                      <Button 
                        size="sm" 
                        variant="destructive" 
                        className="absolute top-2 right-2 w-7 h-7 p-0"
                        onClick={() => { setFabricImage(null); setFabricPreview(null); }}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    <Button 
                      className="w-full bg-purple-600 hover:bg-purple-700"
                      onClick={handleApplyFabric}
                      disabled={loading}
                    >
                      {loading ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {isUSA ? 'Applying...' : 'Aplicando...'}</>
                      ) : (
                        <><Sparkles className="w-4 h-4 mr-2" /> {isUSA ? 'Apply Fabric' : 'Aplicar Tela'}</>
                      )}
                    </Button>
                  </div>
                ) : (
                  <Button 
                    variant="outline" 
                    className="w-full h-16 border-dashed border-purple-300 hover:border-purple-400 hover:bg-purple-100"
                    onClick={() => fabricInputRef.current?.click()}
                  >
                    <Upload className="w-5 h-5 mr-2" />
                    {isUSA ? 'Upload Fabric Photo' : 'Subir Foto de Tela'}
                  </Button>
                )}

                {/* Change Color Button - shows when there's a design */}
                {(currentDesign || fabricVariation) && (
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <Button 
                      variant="outline" 
                      className="w-full border-amber-300 hover:border-amber-400 hover:bg-amber-50 text-amber-700"
                      onClick={() => setShowColorChange(true)}
                    >
                      <Palette className="w-4 h-4 mr-2" />
                      {isUSA ? 'Change Color' : 'Cambiar Color'}
                    </Button>
                    <p className="text-xs text-slate-500 mt-2 text-center">
                      {isUSA ? 'Generate same design in a different color' : 'Generar el mismo diseño en otro color'}
                    </p>
                  </div>
                )}
              </div>

              {/* Back Button */}
              <Button 
                variant="ghost" 
                className="w-full"
                onClick={() => setStep(1)}
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                {isUSA ? 'Create New Design' : 'Crear Nuevo Diseño'}
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Image Modal - Using Portal to render outside Dialog */}
      {imageModal && createPortal(
        <div 
          id="image-modal-overlay"
          style={{ position: 'fixed', inset: 0, zIndex: 99999, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
          onMouseDown={(e) => {
            if (e.target.id === 'image-modal-overlay') {
              setImageModal(null);
            }
          }}
        >
          <div className="relative max-w-4xl w-full" onMouseDown={e => e.stopPropagation()}>
            <div className="bg-white rounded-xl overflow-hidden">
              {/* Close button inside modal header */}
              <div className="flex items-center justify-between p-3 border-b bg-slate-50">
                <p className="font-medium text-slate-900 truncate">{imageModal.title}</p>
                <button 
                  type="button"
                  className="p-2 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-200"
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    setImageModal(null);
                  }}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <img 
                src={imageModal.src}
                alt={imageModal.title}
                className="w-full max-h-[70vh] object-contain"
              />
              <div className="p-4 border-t flex items-center justify-center gap-3">
                <Button 
                  variant="outline"
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    const link = document.createElement('a');
                    link.href = imageModal.src;
                    link.download = `${imageModal.title.replace(/\s+/g, '_')}.png`;
                    link.click();
                  }}
                >
                  <Download className="w-4 h-4 mr-2" />
                  {isUSA ? 'Download' : 'Descargar'}
                </Button>
                <button 
                  type="button"
                  data-testid="close-image-modal"
                  className="px-4 py-2 rounded-md text-slate-600 hover:bg-slate-100 border"
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    setImageModal(null);
                  }}
                >
                  {isUSA ? 'Close' : 'Cerrar'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Save Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isUSA ? 'Save to Catalog' : 'Guardar en Catálogo'}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label>{isUSA ? 'Product Name' : 'Nombre del Producto'}</Label>
            <Input 
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
              placeholder={isUSA ? 'E.g.: Summer Dress Collection' : 'Ej: Vestido Colección Verano'}
              className="mt-2"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveDialog(false)}>
              {isUSA ? 'Cancel' : 'Cancelar'}
            </Button>
            <Button 
              onClick={handleSaveToCatalog}
              disabled={saving || !productName.trim()}
              className="bg-green-600 hover:bg-green-700"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {isUSA ? 'Save' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Color Modal */}
      <Dialog open={showColorChange} onOpenChange={setShowColorChange}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Palette className="w-5 h-5 text-amber-600" />
              {isUSA ? 'Change Color' : 'Cambiar Color'}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {/* Color Input Type Selection */}
            <div>
              <Label className="text-sm font-medium">{isUSA ? 'How do you want to specify the color?' : '¿Cómo quieres especificar el color?'}</Label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                <button
                  onClick={() => setColorInputType('text')}
                  className={`p-3 rounded-lg border-2 text-center transition-all ${
                    colorInputType === 'text' 
                      ? 'border-amber-500 bg-amber-50 text-amber-700' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <span className="text-lg block mb-1">📝</span>
                  <span className="text-xs font-medium">{isUSA ? 'Text' : 'Texto'}</span>
                </button>
                <button
                  onClick={() => setColorInputType('pantone')}
                  className={`p-3 rounded-lg border-2 text-center transition-all ${
                    colorInputType === 'pantone' 
                      ? 'border-amber-500 bg-amber-50 text-amber-700' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <span className="text-lg block mb-1">🎨</span>
                  <span className="text-xs font-medium">Pantone</span>
                </button>
                <button
                  onClick={() => setColorInputType('photo')}
                  className={`p-3 rounded-lg border-2 text-center transition-all ${
                    colorInputType === 'photo' 
                      ? 'border-amber-500 bg-amber-50 text-amber-700' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <span className="text-lg block mb-1">📷</span>
                  <span className="text-xs font-medium">{isUSA ? 'Photo' : 'Foto'}</span>
                </button>
              </div>
            </div>

            {/* Text Input */}
            {colorInputType === 'text' && (
              <div>
                <Label>{isUSA ? 'Color Description' : 'Descripción del Color'}</Label>
                <Input
                  value={colorInput}
                  onChange={(e) => setColorInput(e.target.value)}
                  placeholder={isUSA ? 'E.g.: Navy blue, burgundy, forest green...' : 'Ej: Azul marino, vino, verde bosque...'}
                  className="mt-1"
                />
              </div>
            )}

            {/* Pantone Input */}
            {colorInputType === 'pantone' && (
              <div>
                <Label>{isUSA ? 'Pantone Code' : 'Código Pantone'}</Label>
                <Input
                  value={colorInput}
                  onChange={(e) => setColorInput(e.target.value)}
                  placeholder={isUSA ? 'E.g.: Pantone 19-4052, Classic Blue...' : 'Ej: Pantone 19-4052, Classic Blue...'}
                  className="mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">
                  {isUSA ? 'Enter Pantone code or name' : 'Ingresa el código o nombre Pantone'}
                </p>
              </div>
            )}

            {/* Photo Reference */}
            {colorInputType === 'photo' && (
              <div>
                <Label>{isUSA ? 'Color Reference Photo' : 'Foto de Referencia de Color'}</Label>
                <input
                  type="file"
                  accept="image/*"
                  ref={colorReferenceInputRef}
                  onChange={handleColorReferenceUpload}
                  className="hidden"
                />
                {colorReferencePreview ? (
                  <div className="relative mt-2">
                    <img 
                      src={colorReferencePreview} 
                      alt="Color reference" 
                      className="w-full h-32 object-cover rounded-lg border"
                    />
                    <Button 
                      size="sm" 
                      variant="destructive" 
                      className="absolute top-2 right-2 w-7 h-7 p-0"
                      onClick={() => { setColorReferenceImage(null); setColorReferencePreview(null); }}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <Button 
                    variant="outline" 
                    className="w-full h-24 border-dashed mt-2"
                    onClick={() => colorReferenceInputRef.current?.click()}
                  >
                    <Upload className="w-5 h-5 mr-2" />
                    {isUSA ? 'Upload color reference' : 'Subir referencia de color'}
                  </Button>
                )}
                <p className="text-xs text-slate-500 mt-2">
                  {isUSA ? 'Upload a photo with the color you want to use' : 'Sube una foto con el color que quieres usar'}
                </p>
              </div>
            )}

            {/* Preview of result */}
            {colorResult && (
              <div className="border-t pt-4">
                <Label className="text-sm font-medium">{isUSA ? 'Result' : 'Resultado'}</Label>
                <p className="text-xs text-slate-400 mb-2">{isUSA ? 'Click to enlarge' : 'Click para ampliar'}</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-slate-500 mb-1 text-center">{isUSA ? 'Original' : 'Original'}</p>
                    <img 
                      src={`data:image/jpeg;base64,${fabricVariation?.image_base64 || currentDesign?.image_base64}`}
                      alt="Original"
                      className="w-full aspect-square object-cover rounded-lg border cursor-pointer hover:border-slate-400 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        setImageModal({
                          src: `data:image/jpeg;base64,${fabricVariation?.image_base64 || currentDesign?.image_base64}`,
                          title: isUSA ? 'Original Design' : 'Diseño Original'
                        });
                      }}
                    />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1 text-center">{isUSA ? 'New Color' : 'Nuevo Color'}</p>
                    <img 
                      src={`data:image/jpeg;base64,${colorResult}`}
                      alt="New color"
                      className="w-full aspect-square object-cover rounded-lg border border-amber-400 cursor-pointer hover:border-amber-500 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        setImageModal({
                          src: `data:image/jpeg;base64,${colorResult}`,
                          title: isUSA ? 'New Color' : 'Nuevo Color'
                        });
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            {colorResult ? (
              <>
                <Button variant="outline" onClick={() => setColorResult(null)}>
                  {isUSA ? 'Try Another Color' : 'Probar Otro Color'}
                </Button>
                <Button 
                  className="bg-green-600 hover:bg-green-700"
                  onClick={applyColorResult}
                >
                  <Check className="w-4 h-4 mr-2" />
                  {isUSA ? 'Apply This Color' : 'Aplicar Este Color'}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setShowColorChange(false)}>
                  {isUSA ? 'Cancel' : 'Cancelar'}
                </Button>
                <Button 
                  onClick={handleColorChange}
                  disabled={generatingColor || (!colorInput && !colorReferenceImage)}
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  {generatingColor ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {isUSA ? 'Generating...' : 'Generando...'}</>
                  ) : (
                    <><Sparkles className="w-4 h-4 mr-2" /> {isUSA ? 'Generate' : 'Generar'}</>
                  )}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FashionDesigner;
